import { useCallback, useMemo } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Interval } from "luxon"

interface UseSaveDateRangeProps {
  id: string
}

interface UseSaveDateRangeValue {
  saveDateRange: (dateRange: Interval) => void
  savedDateRange: Interval | null
}

/**
 * Manages the date range in the persistence layer.
 *
 * @example
 * const {
 *   savedDateRange,
 *   saveDateRange,
 * } = useSaveDateRange({
 *   id: "sitesOverviewDateRange",
 * })
 */
export const useSaveDateRange = ({
  id,
}: UseSaveDateRangeProps): UseSaveDateRangeValue => {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => [id], [id])

  const { data } = useQuery({
    gcTime: Infinity,
    queryFn: async (): Promise<{ end: string; start: string } | null> =>
      queryClient.getQueryData(queryKey) ?? null,
    queryKey,
    select: (rawData) =>
      rawData ? Interval.fromISO(`${rawData.start}/${rawData.end}`) : null,
    staleTime: Infinity,
  })

  const saveDateRange = useCallback(
    (dateRange: Interval) => {
      queryClient.setQueryData(queryKey, {
        start: dateRange.start?.toISODate(),
        end: dateRange.end?.toISODate(),
      })
      void queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey]
  )

  return {
    saveDateRange,
    savedDateRange: data ?? null,
  }
}
